import { useId } from "react";

import { cn } from "@/lib/utils";

export function ChevronIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("h-1 w-2 text-white", className)}
      viewBox="0 0 8 4"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6.774.178L3.996 2.535 1.22.178a.806.806 0 00-1.01 0 .543.543 0 000 .856l3.286 2.788c.28.237.73.237 1.01 0L7.79 1.034a.543.543 0 000-.856c-.28-.231-.738-.237-1.017 0z"
        fill="currentColor"
      />
    </svg>
  );
}

export function DayIcon({ className, ...props }: React.ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("h-2.5 w-3 text-white", className)}
      fill="none"
      viewBox="0 0 12 10"
      {...props}
    >
      <rect
        height="9"
        rx="0.5"
        stroke="currentColor"
        width="11"
        x="0.5"
        y="0.5"
      />
      <path d="M4 1v8m4-8v8" stroke="currentColor" />
    </svg>
  );
}

export function MoonIcon({ className, ...props }: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-4 w-[17px] text-amber", className)}
      fill="none"
      viewBox="0 0 17 16"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id})`}>
        <path
          d="M10.547 9.625a3.8 3.8 0 01-1.348-.227 2.893 2.893 0 01-1.031-.652 2.871 2.871 0 01-.66-1.027 3.73 3.73 0 01-.23-1.344 4.115 4.115 0 01.109-.926c.034-.15.073-.28.117-.386a.396.396 0 00.04-.156.203.203 0 00-.056-.137c-.034-.045-.084-.067-.152-.067a.637.637 0 00-.191.035 3.32 3.32 0 00-1.184.813c-.339.36-.603.776-.793 1.25a3.949 3.949 0 00-.281 1.472c0 .537.095 1.03.285 1.477a3.6 3.6 0 00.8 1.168c.344.33.745.587 1.204.77a3.94 3.94 0 001.488.277c.393 0 .77-.056 1.129-.168.36-.11.69-.263.992-.461.302-.198.563-.427.781-.688.222-.26.39-.54.504-.84a.375.375 0 00.028-.109.555.555 0 00.007-.078.198.198 0 00-.066-.156.195.195 0 00-.14-.063.525.525 0 00-.145.028c-.14.047-.32.09-.54.133a3.52 3.52 0 01-.667.062z"
          fill="currentColor"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="15.262"
          id={id}
          width="15.219"
          x="0.887"
          y="0.703"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_7187_29930"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_7187_29930"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function SwitchViewIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("h-[15px] w-4.5 text-white", className)}
      viewBox="0 0 18 15"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M3.766 14.953h10.46c.902 0 1.6-.25 2.094-.75.5-.495.75-1.193.75-2.094V3.234c0-.895-.25-1.593-.75-2.093-.495-.5-1.192-.75-2.093-.75H3.766c-.896 0-1.594.25-2.094.75-.5.5-.75 1.198-.75 2.093v8.875c0 .901.25 1.6.75 2.094.5.5 1.198.75 2.094.75zm.343-2.492c-.234 0-.408-.052-.523-.156-.115-.11-.172-.29-.172-.54V5.509c0-.25.057-.43.172-.54.115-.109.289-.163.523-.163h9.774c.234 0 .409.054.523.164.115.11.172.289.172.539v6.258c0 .25-.057.43-.172.539-.114.104-.289.156-.523.156H4.109zm3.547-5.453h.43c.14 0 .24-.026.297-.078.062-.058.094-.157.094-.297v-.438c0-.14-.032-.237-.094-.289-.057-.052-.156-.078-.297-.078h-.43c-.146 0-.247.026-.304.078-.053.052-.079.149-.079.29v.437c0 .14.026.24.079.297.057.052.158.078.304.078zm2.266 0h.43c.14 0 .24-.026.296-.078.058-.058.086-.157.086-.297v-.438c0-.14-.028-.237-.086-.289-.057-.052-.156-.078-.296-.078h-.43c-.146 0-.248.026-.305.078-.057.052-.086.149-.086.29v.437c0 .14.029.24.086.297.057.052.159.078.305.078zm2.258 0h.43c.14 0 .239-.026.296-.078.063-.058.094-.157.094-.297v-.438c0-.14-.031-.237-.094-.289-.057-.052-.156-.078-.297-.078h-.43c-.14 0-.239.026-.296.078-.057.052-.086.149-.086.29v.437c0 .14.028.24.086.297.057.052.156.078.297.078zM5.39 9.227h.438c.136 0 .232-.026.29-.079.062-.057.093-.153.093-.289v-.437c0-.14-.031-.237-.094-.29-.057-.056-.153-.085-.289-.085h-.437c-.141 0-.24.029-.297.086-.058.052-.086.148-.086.289v.437c0 .136.028.232.086.29.057.052.156.078.297.078zm2.266 0h.43c.14 0 .24-.026.297-.079.062-.057.094-.153.094-.289v-.437c0-.14-.032-.237-.094-.29-.057-.056-.156-.085-.297-.085h-.43c-.146 0-.247.029-.304.086-.053.052-.079.148-.079.289v.437c0 .136.026.232.079.29.057.052.158.078.304.078zm2.266 0h.43c.14 0 .24-.026.296-.079.058-.057.086-.153.086-.289v-.437c0-.14-.028-.237-.086-.29-.057-.056-.156-.085-.296-.085h-.43c-.146 0-.248.029-.305.086-.057.052-.086.148-.086.289v.437c0 .136.029.232.086.29.057.052.159.078.305.078zm2.258 0h.43c.14 0 .239-.026.296-.079.063-.057.094-.153.094-.289v-.437c0-.14-.031-.237-.094-.29-.057-.056-.156-.085-.297-.085h-.43c-.14 0-.239.029-.296.086-.057.052-.086.148-.086.289v.437c0 .136.028.232.086.29.057.052.156.078.297.078zm-6.79 2.226h.438c.136 0 .232-.026.29-.078.062-.057.093-.156.093-.297v-.43c0-.14-.031-.236-.094-.289-.057-.057-.153-.086-.289-.086h-.437c-.141 0-.24.03-.297.086-.058.053-.086.149-.086.29v.43c0 .14.028.239.086.296.057.052.156.078.297.078zm2.266 0h.43c.14 0 .24-.026.297-.078.062-.057.094-.156.094-.297v-.43c0-.14-.032-.236-.094-.289-.057-.057-.156-.086-.297-.086h-.43c-.146 0-.247.03-.304.086-.053.053-.079.149-.079.29v.43c0 .14.026.239.079.296.057.052.158.078.304.078zm2.266 0h.43c.14 0 .24-.026.296-.078.058-.057.086-.156.086-.297v-.43c0-.14-.028-.236-.086-.289-.057-.057-.156-.086-.296-.086h-.43c-.146 0-.248.03-.305.086-.057.053-.086.149-.086.29v.43c0 .14.029.239.086.296.057.052.159.078.305.078z"
        fill="currentColor"
      />
    </svg>
  );
}

export function UnavailableIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("h-2.5 w-[11px] text-white", className)}
      viewBox="0 0 11 10"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M.78 9.76a.39.39 0 00.216.12.49.49 0 00.248 0 .462.462 0 00.222-.12l3.891-3.897L9.254 9.76a.47.47 0 00.343.14.436.436 0 00.343-.14.487.487 0 00.14-.349.445.445 0 00-.14-.336L6.043 5.177 9.94 1.28a.445.445 0 00.14-.337.444.444 0 00-.14-.349.466.466 0 00-.343-.14.466.466 0 00-.343.14L5.357 4.492 1.466.594a.546.546 0 00-.222-.12.49.49 0 00-.248 0 .394.394 0 00-.216.12.49.49 0 00-.127.47.433.433 0 00.127.216l3.891 3.897-3.89 3.898a.477.477 0 00-.134.216.516.516 0 000 .253.415.415 0 00.133.216z"
        fill="currentColor"
      />
    </svg>
  );
}

export function WeekIcon({ className, ...props }: React.ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("h-2 w-[11px] text-white", className)}
      viewBox="0 0 11 8"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0 0h1.505v1.505H0zm3.01 0h1.505v1.505H3.01zm6.022 0h1.505v1.505H9.032zM0 3.01h1.505v1.505H0zm3.01 0h1.505v1.505H3.01zm6.022 0h1.505v1.505H9.032zM6.021 0h1.505v1.505H6.021zm0 3.01h1.505v1.505H6.021zM0 6.021h1.505v1.505H0zm3.01 0h1.505v1.505H3.01zm3.011 0h1.505v1.505H6.021zm3.011 0h1.505v1.505H9.032z"
        fill="currentColor"
      />
    </svg>
  );
}
